import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useTranslation from "../util/useTranslate";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
} from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { CheckCircleFill, Trophy, X as XIcon } from "react-bootstrap-icons";
import ParallaxBackground from "./ParallaxBackground";
import PlayerIcon from "./PlayerIcon";
import useSupabaseRoomSubscription from "util/useSupabaseRoomSubscription";
import { endGame, swapCards, togglePlayerReady } from "util/supabase";

const CardTable = ({ gameSettings, currentPlayer }) => {
  const translate = useTranslation();
  const [chatMessages, setChatMessages] = useState([]);
  const [currentChatMessage, setCurrentChatMessage] = useState("");
  const [cardAnimations, setCardAnimations] = useState([]);
  const [playerSelection, setPlayerSelection] = useState([]);
  const { gameId } = useParams();

  const { broadcastEvent } = useSupabaseRoomSubscription({
    subscriptionId: currentPlayer.id,
    channelName: `GAME_${gameId}`,
    onEvent: ({ event, payload }) => {
      if (event === "chatMessage") {
        setChatMessages([payload, ...chatMessages]);
      } else if ( event === "playerCardTaken") {
        if (payload.playerId !== currentPlayer.id) {

          if(payload.index === playerSelection[1]?.index) {
            setPlayerSelection([playerSelection[0], undefined])
          } 

          setCardAnimations((prev) => [
            ...prev,
            { id: uuidv4(), started: false, ...payload },
          ]);
        }
      }
    },
  });

  const handlePlayerReadyToggle = (e) => {
    togglePlayerReady(gameId, currentPlayer.id);
  };

  useEffect(() => {
    const notStartedAnimationIds = cardAnimations
      .filter((item) => !item.started)
      .map((item) => item.id);
    if (notStartedAnimationIds.length > 0) {
      setCardAnimations((prev) =>
        prev.map((item) =>
          notStartedAnimationIds.includes(item.id)
            ? { ...item, started: true }
            : item
        )
      );
      notStartedAnimationIds.forEach((item) => {
        setTimeout(() => {
          setCardAnimations((prev) =>
            prev.filter((animation) => animation.id !== item)
          );
        }, 1100);
      });
    }
  }, [cardAnimations]);

  const sendChatMessage = (e) => {
    e.preventDefault();
    broadcastEvent("chatMessage", {
      id: currentPlayer.id,
      name: currentPlayer.name,
      message: currentChatMessage,
      team: currentPlayer.team,
    });
    setCurrentChatMessage("");
  };

  const handleEndGameGuess = (guess, doubleScore = false) => {
    endGame(gameId, currentPlayer.id, guess, doubleScore);
  };

  const playersReadyCount = useMemo(
    () => gameSettings.players.filter((item) => item.isReady).length,
    [gameSettings]
  );

  const cardAnimationsTablePositions = useMemo(() => {
    let animations = cardAnimations.filter((item) => item.started);
    let tablePositions = Array(4).fill([]);
    animations.forEach((item) => {
      tablePositions[item.index] = [...tablePositions[item.index], item];
    });
    return tablePositions;
  }, [cardAnimations]);

  const getTeamColor = (team) => {
    return team === 1 ? "red" : "blue";
  };

  const getPlayerIndex = useCallback(
    (id) => gameSettings.players.findIndex((item) => item.id === id),
    [gameSettings.players]
  );

  const doPlayerSelection = (isHand, cardId, cardIndex) => {
    const index = isHand ? 0 : 1;
    const newValue = [...playerSelection];
    newValue[index] = newValue[index] ? undefined : { id: cardId, index: cardIndex };

    if(newValue[0] && newValue[1]) {
      swapCards(gameId, currentPlayer.id, newValue[0].id, newValue[1].id).then((result) => {

      if(result) {
          broadcastEvent("playerCardTaken", {
            playerId: currentPlayer.id,
            index: newValue[1].index,
            playerName: currentPlayer.name,
            team: currentPlayer.team,
          });
        }

        setPlayerSelection([]);
      })
    } else {
      setPlayerSelection(newValue);
    }
  }

  return (
    <>
      <style type="text/css">
        {`
          @keyframes nameMove {
            from {top: 20vh;}
            to {top: 0;}
          }

          .img-fluid {
            transition: transform .3s;
            cursor: pointer;
          }
          .img-fluid.selected {
            transform: scale(1.1);
          }

          .animatedName {
            top: 0;
            animation-name: nameMove;
            animation-duration: 1s;
            position: absolute;
            font-weight: bold;
            z-index: 2;
          }
      `}
      </style>

      <Container>
        <Row className="justify-content-center d-flex py-4">
          <h4 style={{ width: "auto" }}>{translate("table")}</h4>
        </Row>
        <Row>
          {" "}
          {cardAnimationsTablePositions.map((item, index) => (
            <Col
              key={index}
              style={{ position: "relative" }}
              xs={3}
              className="h-0 p-0"
            >
              {item.map((animation) => (
                <div
                  key={animation.id}
                  style={{ color: getTeamColor(animation.team) }}
                  className="animatedName w-100 text-center"
                >
                  {animation.playerName}
                </div>
              ))}
            </Col>
          ))}
        </Row>

        <Row style={{ height: "30vh" }}>
          {gameSettings.tablecards.map((item, index) => (
            <Col
              style={{ position: "relative" }}
              key={item}
              xs={3}
              className="h-100 p-0 justify-content-center d-flex"
            >
              <Image
                onClick={() => doPlayerSelection(false, item, index)}
                className={`h-100 ${
                  item === playerSelection[1]?.id ? "selected" : undefined
                }`}
                fluid
                style={{ objectFit: "contain" }}
                src={`/cards/${item}.jpg`}
              />
            </Col>
          ))}
        </Row>
        <Row style={{ height: "30vh" }}>
          {currentPlayer.cardHand.map((item) => (
            <Col
              key={item}
              xs={3}
              className="h-100 p-0 justify-content-center d-flex"
            >
              <Image
                onClick={() => doPlayerSelection(true, item)}
                className={`h-100 ${
                  item === playerSelection[0]?.id ? "selected" : undefined
                }`}
                fluid
                style={{ objectFit: "contain" }}
                src={`/cards/${item}.jpg`}
              />
            </Col>
          ))}
        </Row>
        <Row className="d-flex justify-content-center my-4">
          <h4 style={{ width: "auto" }}>{translate("myHand")}</h4>
        </Row>
        <div style={{ minHeight: 190 }}>
          <Row className="h-100 d-flex justify-content-center">
            <Row className="h-100 d-flex justify-content-center">
              <Card
                className="mb-2"
                style={{ overflowY: "auto", height: "120px" }}
              >
                <Card.Body className="d-flex flex-column-reverse gap-2">
                  {chatMessages.map(({ id, name, message, team }, index) => (
                    <div className="d-flex align-items-center gap-2" key={index}>
                      <PlayerIcon
                        inline
                        playerIndex={getPlayerIndex(id)}
                        active
                      />
                      <span style={{ color: getTeamColor(team) }}>
                        <b>{name}</b>:
                      </span>{" "}
                      {message}
                    </div>
                  ))}
                </Card.Body>
              </Card>
              <form style={{ padding: 0 }} onSubmit={sendChatMessage}>
                <Row>
                  <Col xs={9}>
                    {" "}
                    <Form.Control
                      type="text"
                      value={currentChatMessage}
                      onChange={(e) => setCurrentChatMessage(e.target.value)}
                      placeholder={translate("writeMessage")}
                    />
                  </Col>
                  <Col xs={3}>
                    <Button type="submit" className="w-100" variant="primary">
                      {translate("send")}
                    </Button>
                  </Col>
                </Row>
              </form>
            </Row>
            <Row className="mt-2 mb-2" style={{ paddingRight: 0}}>
              <Row className="gap-2" style={{ paddingRight: 0}}>
                <Col style={{ padding: 0 }} sm={4}>
                  <Button
                    className="w-100 mb-2"
                    variant={currentPlayer.isReady ? "success" : "primary"}
                    onClick={handlePlayerReadyToggle}
                  >
                    {translate("ready")} {playersReadyCount}/4{" "}
                    <CheckCircleFill />
                  </Button>
                </Col>
                <Col>
                  <Row className="gap-2">
                    <Col style={{ padding: 0 }}>
                      <Button
                        className="w-100"
                        variant="success"
                        disabled={gameSettings.isfirstround}
                        onClick={() => handleEndGameGuess(true, true)}
                      >
                        {translate("winCallDouble")} <Trophy /><Trophy />
                      </Button>
                    </Col>
                    <Col style={{ padding: 0 }}>
                      <Button
                        className="w-100"
                        variant="success"
                        disabled={gameSettings.isfirstround}
                        onClick={() => handleEndGameGuess(true)}
                      >
                        {translate("winCall")} <Trophy />
                      </Button>
                    </Col>
                  </Row>
                  <Row>
                    <Button
                      className="w-100 mt-1 mb-2"
                      variant="danger"
                      disabled={gameSettings.isfirstround}
                      onClick={() => handleEndGameGuess(false)}
                    >
                      {translate("loseCall")} <XIcon />
                    </Button>
                  </Row>
                </Col>
              </Row>
            </Row>
          </Row>
        </div>
      </Container>
      <ParallaxBackground
        src={"/backgrounds/table.png"}
        position={"bottom left"}
        size={"100vw"}
      />
    </>
  );
};

export default CardTable;
